import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Heading,
 Flex,
 Box,
 Progress,
 Text,
 Accordion,
 AccordionPanel,
 AccordionItem,
 AccordionButton,
 AccordionIcon,
 useToast,
} from '@chakra-ui/react';
import { QuestionCardReviewer, QuestionCardSimple, PillarSelectorRow } from '../../components/Card';
import baseService from '../../services/base';
import { useSSO } from '../../context/SSO';
import { AttachEvidenceModal } from '../../components/Modals';
import IncludeDates from '../../components/Datepicker/IncludeDates';
import { DownloadResponsesButton, EvidenceDownloadButton } from '../../components/Button';
import { downloadFile } from '../../helpers/downloadFile';
import { useResponsesDownload } from '../../hooks/useResponsesDownload';
import useAuditHeaderData, { useIsAudit } from '../audit/useAuditHeaderData';

async function fetch(url) {
 const response = await baseService.get(url);
 if (!response?.data || Object.keys(response?.data)?.length === 0) {
  throw new Error('Empty response data');
 }
 return response.data;
}

function convertToTimestamp(dateString) {
 if (!dateString) {
  return null;
 }
 const [year, month] = dateString.split('-').map(Number);
 const date = new Date(year, month - 1);
 return date.getTime();
}

function formatDateToString(date) {
 const year = date.getFullYear();
 const month = (date.getMonth() + 1).toString().padStart(2, '0');
 return `${year}-${month}`;
}

export default function PeriodicType({
 companyId,
 surveyId,
 companyGroupId,
 formName,
 selectedCompanyId,
 isResponsibleUser = false,
 mutate,
 openChat,
}) {
 const toast = useToast();
 const { user } = useSSO();
 const isAudit = useIsAudit();
 const { downloadResponses, isLoadingResponses: isDownloadingResponses } =
  useResponsesDownload();
 const [evidenceState, setEvidenceState] = useState({
  isOpen: false,
  currentQuestionId: null,
  currentFiles: [],
 });
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [isLoadingEvidence, setLoadingEvidence] = useState(false);
 const [selectedPillar, setSelectedPillar] = useState();
 const [periodSelected, setPeriodSelected] = useState();
 const [formattedPeriodSelected, setFormattedPeriodSelected] = useState();
 const [themeIndex, setThemeIndex] = useState(0);

 const { data, isLoading } = useSWR(
  `survey/periodic/${surveyId}/survey?id_company=${companyId}&period=${formattedPeriodSelected}`,
  fetch
 );

 const { data: currentPillarData } = useSWR(
  selectedPillar && periodSelected && formattedPeriodSelected
   ? `survey/admin/periodic/anwsered?period=${formattedPeriodSelected}&id_company=${companyId}&id_pillar=${selectedPillar}&id_survey=${surveyId}`
   : null,
  fetch
 );

 useEffect(() => {
  if (data) {
   if (data.pillars?.length > 0) {
    let idPillar = data.pillars[0].id_pillar;
    let indexTheme = 0;
    if (!selectedPillar) {
     if (openChat && openChat.themeId) {
      data.pillars.forEach((p) => {
       let exist = p.themes.filter((theme) => theme.id_theme === openChat.themeId);
       if (exist !== undefined && exist.length > 0) {
        idPillar = p.id_pillar;
        indexTheme = p.themes.findIndex((theme) => theme.id_theme === openChat.themeId);
       }
      });
     }
     setThemeIndex(indexTheme);
     setSelectedPillar(idPillar);
    }
   }

   const dateToUse = data.period_selected?.month || data.period?.start_date;
   if (dateToUse) {
    const timestamp = convertToTimestamp(dateToUse);
    if (timestamp) {
     setPeriodSelected(timestamp);
     setFormattedPeriodSelected(dateToUse);
    }
   } else {
    const currentDate = new Date();
    const defaultFormatted = formatDateToString(currentDate);
    setPeriodSelected(currentDate.getTime());
    setFormattedPeriodSelected(defaultFormatted);
   }
  }
 }, [data]);

 const pillarOptions = useMemo(() => {
  if (!data?.pillars?.length) return [];

  return data.pillars.map((pillar) => ({
   label: pillar.name_pillar,
   value: pillar.id_pillar,
  }));
 }, [data]);

 const periods = useMemo(() => {
  if (!data?.period?.periods?.length) return [];

  return data.period.periods
   .filter((period) => period.month) 
   .map((period) => convertToTimestamp(period.month))
   .filter((timestamp) => timestamp !== null); 
 }, [data]);

 const selectedPillarData = useMemo(() => {
  return data?.pillars?.find((pillar) => pillar?.id_pillar === selectedPillar);
 }, [selectedPillar, data]);

 // findPeriodic não devolve img_pillar (só findDiagnostic devolve) — o card
 // cai no fallback sem capa do PillarSelectorRow, igual ao form quando a
 // capa está ausente.
 const normalizedPillars = useMemo(() => {
  if (!data?.pillars?.length) return [];
  return data.pillars.map((pillar) => {
   const questions = pillar?.themes?.flatMap((theme) => theme?.questions || []) || [];
   return {
    id_pillar: pillar.id_pillar,
    name_pillar: pillar.name_pillar,
    img_pillar: pillar.img_pillar,
    totalQuestions: questions.length,
    answeredQuestions: questions.filter((question) =>
     question?.users_responses?.some(
      (response) => response?.company?.id_company === +selectedCompanyId
     )
    ).length,
   };
  });
 }, [data, selectedCompanyId]);

 const themeProgress = useMemo(() => {
  const progress = {};
  currentPillarData?.pillars?.[0]?.themes?.forEach((theme) => {
   const questions = theme?.questions || [];
   progress[theme?.id_theme] = {
    totalQuestions: questions.length,
    answeredQuestions: questions.filter((question) =>
     question?.users_responses?.some(
      (response) => response?.company?.id_company === +selectedCompanyId
     )
    ).length,
   };
  });
  return progress;
 }, [currentPillarData, selectedCompanyId]);

 const handleDateChange = useCallback((date) => {
  const timestamp = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
  const formattedDate = formatDateToString(date);
  setPeriodSelected(timestamp);
  setFormattedPeriodSelected(formattedDate);
 }, []);

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setEvidenceState({
   isOpen: true,
   currentQuestionId: questionId,
   currentFiles: response?.response_file,
  });
 }, []);

 async function handleEvidenceDownload() {
  setLoadingEvidence(true);
  try {
   const response = await baseService.get(
    `/downloads?survey=${surveyId}&group=${companyGroupId}&form=${formName}`,
    { responseType: 'arraybuffer' }
   );

   if (response.headers['content-type'].includes('application/json')) {
    const data = JSON.parse(new TextDecoder().decode(response.data));
    downloadFile(data.url, `${formName}_evidence.zip`);
   } else {
    const blob = new Blob([response.data], { type: 'application/zip' });
    downloadFile(window.URL.createObjectURL(blob), `${formName}_evidence.zip`);
   }

   toast({
    title: 'Download das evidências iniciado com sucesso',
    status: 'success',
   });
  } catch (error) {
   toast({
    title: 'Erro ao fazer o download das evidências',
    description: 'Não foi possível processar os arquivos anexados. Tente novamente.',
    status: 'error',
   });
  } finally {
   setLoadingEvidence(false);
  }
 }

 const hasEvidenceFiles = useMemo(() => {
  return currentPillarData?.pillars?.[0]?.themes?.some((theme) =>
   theme?.questions?.some((question) =>
    question?.users_responses?.some(
     (response) =>
      response?.company?.id_company === +selectedCompanyId &&
      response?.response_file?.length > 0
    )
   )
  );
 }, [currentPillarData, selectedCompanyId]);

 // EG-63: em auditoria o sub-header some, MAS o seletor de período
 // (`IncludeDates`) não é decorativo — troca qual período carrega. Fica
 // sempre visível; o resto (download, "Questões") vira ação/rótulo do
 // AuditHeader.
 useAuditHeaderData(
  isAudit
   ? {
      downloadingResponses: isDownloadingResponses,
      downloadingEvidence: isLoadingEvidence,
      evidenceDisabled: !hasEvidenceFiles,
      onDownloadResponses: () =>
       downloadResponses({ surveyId, companyId: selectedCompanyId, companyGroupId, formName }),
      onDownloadEvidence: () => handleEvidenceDownload(),
     }
   : null
 );

 if (!pillarOptions?.length || !selectedPillar) {
  return null;
 }

 if (isLoading || !selectedPillar) {
  return null;
 }

 return (
  <Box>
   {isAudit ? (
    <Flex mb='14px' justify='flex-end'>
     <IncludeDates
      periods={periods}
      periodSelected={periodSelected}
      onChange={handleDateChange}
     />
    </Flex>
   ) : (
   <Flex bg='white' w='100%' p='4' borderRadius='12px' mt='24px'>
    <Flex w='full' align='center' justify='space-between' gap='6'>
     <Box flexShrink={0} display='inline-flex' flex='1' maxW='120px'>
      <Heading as='h3' fontSize='md'>
       Questões
      </Heading>
     </Box>

     <Flex flex='3' align='center' justifyContent='flex-end' gap='20px'>
      <IncludeDates
       periods={periods}
       periodSelected={periodSelected}
       onChange={handleDateChange}
      />
      {!isResponsibleUser && (
       <>
        <DownloadResponsesButton
         surveyId={surveyId}
         companyId={selectedCompanyId}
         companyGroupId={companyGroupId}
         formName={formName}
         isLoadingResponses={isLoadingResponses}
         setLoadingResponses={setLoadingResponses}
        />

        <Flex w='26%' maxW='260px' overflowX='hidden'>
         <EvidenceDownloadButton
          isLoading={isLoadingEvidence}
          isDisabled={!hasEvidenceFiles}
          onClick={handleEvidenceDownload}
         />
        </Flex>
       </>
      )}
     </Flex>
    </Flex>
   </Flex>
   )}

   {selectedPillar && (
    <Box mt={isAudit ? 0 : '16px'}>
     <PillarSelectorRow
      pillars={normalizedPillars}
      selectedPillar={selectedPillar}
      onSelect={setSelectedPillar}
     />
    </Box>
   )}

   <Accordion
    allowMultiple
    defaultIndex={[themeIndex]}
    bg='white'
    borderRadius='12px'
    p='6'
    mt='6'
   >
    {selectedPillarData &&
     currentPillarData &&
     currentPillarData?.pillars?.length > 0 &&
     currentPillarData?.pillars?.[0]?.themes?.map((theme, index) => (
      <AccordionItem key={theme?.id_theme} bg='white' border='none' mb='16px'>
       {({ isExpanded }) => {
        const progress = themeProgress[theme?.id_theme] || {
         totalQuestions: 0,
         answeredQuestions: 0,
        };
        const isThemeComplete =
         progress.totalQuestions > 0 && progress.answeredQuestions === progress.totalQuestions;

        return (
        <>
         <AccordionButton
          h='56px'
          borderRadius='0'
          bg={isExpanded ? '#E6F1F8' : '#F5F5FB'}
          p='16px'
          borderLeft='4px solid'
          borderLeftColor={isThemeComplete ? '#598F3C' : '#D4D4D4'}
          _hover={{
           bg: isExpanded ? '#E6F1F8' : '#F5F5FB',
          }}
         >
          <Box
           as='span'
           flex='1'
           textAlign='left'
           color={isExpanded ? '#0073BB' : '#5C6066'}
           fontWeight={600}
           whiteSpace='nowrap'
          >
           {theme?.title}
          </Box>

          {progress.totalQuestions > 0 && (
           <Flex align='center' gap='10px' mr='16px' minW='140px'>
            <Box w='80px'>
             <Progress
              value={(progress.answeredQuestions / progress.totalQuestions) * 100}
              h='6px'
              borderRadius='21px'
              colorScheme='green'
              bg='#D4D4D4'
             />
            </Box>
            <Text fontSize='sm' color='#5C6066' whiteSpace='nowrap'>
             {progress.answeredQuestions}/{progress.totalQuestions}
            </Text>
           </Flex>
          )}

          <AccordionIcon />
         </AccordionButton>

         <AccordionPanel p={0}>
          {theme?.questions?.map((question) =>
           isResponsibleUser ? (
            <QuestionCardReviewer
             key={question?.id_question}
             question={question}
             handleQuestionEvidence={handleQuestionEvidence}
             surveyId={surveyId}
             mutate={mutate}
             questionnaireType='periodic'
            />
           ) : (
            <QuestionCardSimple
             key={question?.id_question}
             question={question}
             selectedCompanyId={selectedCompanyId}
             handleQuestionEvidence={handleQuestionEvidence}
             surveyId={surveyId}
             themeId={theme?.id_theme}
             userId={user?.user?.id_user}
             questionnaireType='periodic'
             openChat={openChat && openChat.questionId === question.id_question}
            />
           )
          )}
         </AccordionPanel>
        </>
        );
       }}
      </AccordionItem>
     ))}
   </Accordion>

   <AttachEvidenceModal
    isOpen={evidenceState.isOpen}
    onClose={() =>
     setEvidenceState({
      isOpen: false,
      currentQuestionId: null,
      currentFiles: [],
     })
    }
    questionId={evidenceState.currentQuestionId}
    files={evidenceState.currentFiles}
   />
  </Box>
 );
}
